import React from 'react'
import { Chip } from '@material-ui/core'

// ── Enums (mirroring model) ────────────────────────────────────────────────────

export const PRICING_MODES = ['RETAIL', 'WHOLESALE', 'RESELLER', 'PIT_PICKUP', 'YARD_PICKUP', 'DELIVERED', 'UNKNOWN']
export const MAPPING_STATUSES = ['AUTO_MAPPED', 'MANUALLY_MAPPED', 'NEEDS_REVIEW', 'UNMATCHED', 'IGNORED']
export const UNIT_OPTIONS = ['TON', 'EACH', 'LB', 'SQFT', 'CY', 'UNKNOWN']
export const PAGE_SIZE = 25

export const COLOR_FAMILIES = ['BROWN', 'GOLD', 'RED', 'GRAY', 'WHITE', 'BLACK', 'TAN', 'PINK', 'GREEN', 'MIXED', 'UNKNOWN']
export const MATERIAL_CATEGORIES = ['DECORATIVE_ROCK', 'CONSTRUCTION_AGGREGATE', 'RIP_RAP', 'SAND', 'BOULDER', 'FLAGSTONE', 'SOIL_OR_PLANTING_MIX', 'UNKNOWN']

export const CATEGORY_TO_AGGREGATE_TYPES = {
  DECORATIVE_ROCK: ['SCREENED', 'DECO', 'MINUS', 'WASHED'],
  CONSTRUCTION_AGGREGATE: ['SCREENED', 'MINUS', 'ABC', 'WASHED'],
  RIP_RAP: ['RIP_RAP'],
  SAND: ['SAND'],
  BOULDER: ['BOULDER'],
  FLAGSTONE: ['FLAGSTONE'],
  SOIL_OR_PLANTING_MIX: ['SOIL_OR_PLANTING_MIX'],
  UNKNOWN: null
}

export const EMPTY_CREATE_FORM = {
  vendorId: '',
  supplierLocationId: '',
  selectedMaterial: null,
  selectedGradation: null,
  pricingMode: 'UNKNOWN',
  price: '',
  unit: 'TON'
}

// ── Formatters ─────────────────────────────────────────────────────────────────

export function formatPrice (price, unit) {
  if (price == null) return '\u2014'
  return `$${Number(price).toFixed(2)} / ${unit || 'TON'}`
}

export function formatMoney (price) {
  if (price == null || Number.isNaN(Number(price))) return '\u2014'
  return `$${Number(price).toFixed(2)}`
}

export function formatOfferingLabel (offering) {
  if (!offering) return ''
  const supplier = offering.vendorId && offering.vendorId.name ? offering.vendorId.name : 'Unknown'
  const material = offering.materialProfileId && offering.materialProfileId.displayName
    ? offering.materialProfileId.displayName
    : (offering.productTitle || 'Material')
  const gradation = offering.gradationId && offering.gradationId.displayName
    ? offering.gradationId.displayName
    : (offering.variantTitle || '')
  const price = offering.price != null ? ` - ${formatPrice(offering.price, offering.unit)}` : ''
  return `${supplier} - ${material}${gradation ? ` / ${gradation}` : ''}${price}`
}

export function formatJobsiteLabel (jobsite) {
  if (!jobsite) return ''
  const name = jobsite.name || jobsite.address || 'Unnamed jobsite'
  const address = [jobsite.address, jobsite.city, jobsite.state, jobsite.zip].filter(Boolean).join(', ')
  const client = jobsite.client && jobsite.client.companyName ? jobsite.client.companyName : ''
  return [name, client, address].filter(Boolean).join(' - ')
}

export function getJobsiteCoords (jobsite) {
  const coords = jobsite && jobsite.location && jobsite.location.coordinates
  if (Array.isArray(coords) && coords.length === 2 && coords[0] != null && coords[1] != null) {
    return { lng: Number(coords[0]), lat: Number(coords[1]) }
  }
  if (jobsite && jobsite.latitude != null && jobsite.longitude != null) {
    return { lat: Number(jobsite.latitude), lng: Number(jobsite.longitude) }
  }
  return null
}

export function formatMaterialLineLabel (item) {
  if (!item) return ''
  const material = item.material && item.material.displayName ? item.material.displayName : 'Material'
  const gradation = item.gradation && item.gradation.displayName ? item.gradation.displayName : ''
  return `${material}${gradation ? ` / ${gradation}` : ''}`
}

export function loadPlanLabel (option) {
  if (!option || option.trips == null || !option.vehicleCapacity) return ''
  const trips = Number(option.trips)
  const capacity = Number(option.vehicleCapacity)
  const unit = capacity === 1 ? 'ton' : 'tons'
  return `${trips} load${trips === 1 ? '' : 's'}, ${capacity} ${unit} max each`
}

export function scenarioLabel (option) {
  if (!option) return ''
  if (option.scenario === 'VENDOR_DELIVERY') return 'Vendor delivery'
  if (option.scenario === 'OUR_TRUCK_20T') return 'Our dump truck'
  if (option.scenario === 'OUR_TRAILER_10T') return 'Our dump trailer'
  if (option.scenario === 'MATERIAL_ONLY') return 'Material price only'
  return option.scenario || ''
}

export function relativeDate (dateStr) {
  if (!dateStr) return '\u2014'
  const d = new Date(dateStr)
  const now = Date.now()
  const diffMs = now - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 30) return `${diffDays}d ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
  return `${Math.floor(diffDays / 365)}yr ago`
}

// ── Sub-components ─────────────────────────────────────────────────────────────

export function PricingModeChip ({ mode, classes }) {
  const classMap = {
    RETAIL: classes.chipRetail,
    WHOLESALE: classes.chipWholesale,
    PIT_PICKUP: classes.chipPitPickup,
    YARD_PICKUP: classes.chipYardPickup,
    DELIVERED: classes.chipDelivered,
    RESELLER: classes.chipWholesale,
    UNKNOWN: classes.chipUnknown
  }
  return <Chip label={mode} size="small" className={classMap[mode] || classes.chipUnknown} />
}

export function AvailabilityChip ({ status, classes }) {
  if (status === 'IN_STOCK') return <Chip label="In Stock" size="small" className={classes.stockIn} />
  if (status === 'OUT_OF_STOCK') return <Chip label="Out of Stock" size="small" className={classes.stockOut} />
  return <Chip label="Unknown" size="small" className={classes.stockUnknown} />
}
